import { useMemo, useState } from "react";
import { MatchEvent } from "../../../../store/useMatchLogStore";
import { Match } from "../../types";
import { usePlayerStats } from "../../hooks/usePlayerStats";
import { dedupeByStableIdentity } from "../../hooks/useLogIntegrityChecks";
import { useAdvancedMetrics } from "../../hooks/useAdvancedMetrics";
import { usePlayerRatings } from "../../hooks/usePlayerRatings";
import { useIsDesktop } from "../../../../hooks/useIsDesktop";
import { MatchAnalytics } from "../molecules/MatchAnalytics";
import HeatMapSection from "../molecules/HeatMapSection";
import PeriodMapsSection, {
  getRenderedPeriods,
} from "../molecules/PeriodMapsSection";
import IneffectiveBreakdownSection from "../molecules/IneffectiveBreakdownSection";
import PlayerCardPicker from "../molecules/PlayerCardPicker";
import { PlayerStatsTable } from "../molecules/PlayerStatsTable";
import LiveMatchContextBanner from "../molecules/LiveMatchContextBanner";
import AiInsightsPanel from "./AiInsightsPanel";
import { AI_INSIGHTS_ENABLED } from "../../../../lib/featureFlags";
import { parseClockToSeconds } from "../../lib/clockHelpers";

/**
 * Statuses during which the match clock is actually running — mirrors
 * `PublicMatchAnalytics.tsx`'s own `LIVE_STATUSES` (kept as a separate
 * local copy rather than a shared export: both are short, rarely-changing
 * FSM status lists, and duplicating a 5-item literal set is lower risk
 * than adding a new cross-realm import to either file for this one gate).
 * Drives `useAdvancedMetrics`'s live-gated poll (PMA-COCK-098 follow-up).
 */
const LIVE_STATUSES = new Set([
  "Live_First_Half",
  "Live_Second_Half",
  "Live_Extra_First",
  "Live_Extra_Second",
  "Penalties",
]);

interface AnalyticsViewProps {
  match: Match;
  liveEvents: MatchEvent[];
  queuedEvents: MatchEvent[];
  onFieldIds: { home: Set<string>; away: Set<string> };
  effectiveClock: string;
  globalClock: string;
  /** Broadcast-frame clock = the frame events stamp match_clock in (minutes-played end). */
  broadcastMatchClock: string;
  /** Operator/current period (1-4), same frame as `broadcastMatchClock`. */
  currentPeriod?: number;
  /** COMPLETED/PENALTIES: `broadcastMatchClock` is already the continuous total. */
  matchFinished?: boolean;
  ineffectiveClock: string;
  varClock: string;
  timeoutClock: string;
  effectiveTime: number;
  varTimeSeconds: number;
  timeoutTimeSeconds: number;
  ineffectiveBreakdown: any;
  clockMode: "EFFECTIVE" | "INEFFECTIVE";
  isVarActive: boolean;
  isTimeoutActive: boolean;
  t: any;
}

export default function AnalyticsView({
  match,
  liveEvents,
  queuedEvents,
  onFieldIds,
  effectiveClock,
  globalClock,
  broadcastMatchClock,
  currentPeriod,
  matchFinished = false,
  ineffectiveClock,
  varClock,
  timeoutClock,
  effectiveTime,
  varTimeSeconds,
  timeoutTimeSeconds,
  ineffectiveBreakdown,
  clockMode,
  isVarActive,
  isTimeoutActive,
  t,
}: AnalyticsViewProps) {
  const isDesktop = useIsDesktop();
  // PMA-COCK-091: player-stats hide/show disclosure, default visible.
  const [showPlayerStats, setShowPlayerStats] = useState(true);
  // PMA-COCK-205 (EPIC-43): dedupeByStableIdentity guards the one remaining
  // by-design overlap (sendEvent()'s offline branch) — without it, a
  // duplicated event inflates every stat usePlayerStats derives from it.
  const allEvents = useMemo(
    () => dedupeByStableIdentity([...liveEvents, ...queuedEvents]),
    [liveEvents, queuedEvents],
  );
  const playerStats = usePlayerStats(match, allEvents);
  const {
    data: advancedMetrics,
    isLoading: advancedLoading,
    isError: advancedError,
  } = useAdvancedMetrics(match.id, LIVE_STATUSES.has(match.status));
  // EPIC-07 — Per-Player Match Score (Rating Engine). Fetch-and-merge by
  // player_id from the backend; usePlayerStats above stays a pure
  // client-side replay and is NOT the rating's source. A failed/loading
  // fetch never blocks or hides the rest of this (already-working) table.
  const {
    ratingByPlayerId,
    isLoading: ratingLoading,
    isError: ratingError,
  } = usePlayerRatings(match.id, LIVE_STATUSES.has(match.status));

  const advancedPlayerStats = useMemo(() => {
    if (!advancedMetrics?.players?.length) return undefined;
    return Object.fromEntries(
      advancedMetrics.players.map((p) => [
        p.player_id,
        {
          xg: p.xg,
          progressive_passes: p.progressive_passes,
          progressive_carries: p.progressive_carries,
        },
      ]),
    );
  }, [advancedMetrics]);

  // Derive live ineffective seconds from the global clock formula:
  // globalSeconds = effectiveTime + ineffectiveSeconds + timeoutSeconds
  // This avoids using stale match.ineffective_time_seconds from the server.
  const parseGlobalSeconds = (clock: string) => {
    const [mm, rest] = clock.split(":");
    return Number(mm) * 60 + parseFloat(rest || "0");
  };
  const liveIneffectiveSeconds = Math.max(
    0,
    parseGlobalSeconds(globalClock) - effectiveTime - timeoutTimeSeconds,
  );

  const onFieldHomePlayers = useMemo(
    () =>
      (match.home_team.players ?? []).filter((p) => onFieldIds.home.has(p.id)),
    [match, onFieldIds.home],
  );
  const onFieldAwayPlayers = useMemo(
    () =>
      (match.away_team.players ?? []).filter((p) => onFieldIds.away.has(p.id)),
    [match, onFieldIds.away],
  );

  return (
    <div className="mb-6 flex flex-col gap-3">
      <LiveMatchContextBanner
        match={match}
        onFieldHomePlayers={onFieldHomePlayers}
        onFieldAwayPlayers={onFieldAwayPlayers}
        effectiveTime={effectiveTime}
        ineffectiveSeconds={liveIneffectiveSeconds}
        t={t}
      />
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-slate-900/60 border border-slate-800 px-3 py-2">
        <div className="flex items-center gap-2 text-slate-200 text-sm">
          <span className="text-xs uppercase tracking-wide text-slate-400">
            {t("effectiveTime", "Effective Time")}
          </span>
          <span
            className="font-mono font-semibold text-emerald-400"
            data-testid="effective-clock-value"
          >
            {effectiveClock}
          </span>
        </div>
        <div
          className={`flex items-center gap-2 text-sm ${
            clockMode === "INEFFECTIVE" ? "text-rose-300" : "text-slate-200"
          }`}
        >
          <span className="text-xs uppercase tracking-wide text-slate-400">
            {t("ineffectiveTime", "Ineffective")}
          </span>
          <span
            className={`font-mono font-semibold ${
              clockMode === "INEFFECTIVE" ? "text-rose-400 animate-pulse" : ""
            }`}
            data-testid="analytics-ineffective-clock"
          >
            {ineffectiveClock}
          </span>
        </div>
        {(isVarActive || varTimeSeconds > 0) && (
          <div className="flex items-center gap-2 text-amber-300 text-sm">
            <span className="text-xs uppercase tracking-wide text-amber-400">
              {t("varTime", "VAR")}
            </span>
            <span
              className={`font-mono font-semibold ${
                isVarActive ? "animate-pulse" : ""
              }`}
              data-testid="analytics-var-time"
            >
              {varClock}
            </span>
          </div>
        )}
        {isTimeoutActive && (
          <div className="flex items-center gap-2 text-sky-300 text-sm">
            <span className="text-xs uppercase tracking-wide text-sky-400">
              {t("timeout", "Timeout")}
            </span>
            <span className="font-mono font-semibold animate-pulse">
              {timeoutClock}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 text-slate-200 text-sm">
          <span className="text-xs uppercase tracking-wide text-slate-400">
            {t("globalClock", "Global Clock")}
          </span>
          <span className="font-mono font-semibold">{globalClock}</span>
        </div>
      </div>

      {/* Analytics content — unified view. PMA-COCK-188/189 (client wave
          2026-09-13): the cockpit's analytics tab now mirrors the share
          page's own layout — per-period map blocks, then the unchanged
          whole-match "Generales" heat maps, then the relocated "Tiempo
          perdido" section last — so `MatchAnalytics.exportPdf` (which
          captures this same on-screen DOM) exports in that same order. */}
      <MatchAnalytics
        match={match}
        events={allEvents}
        effectiveTime={effectiveTime}
        varTimeSeconds={varTimeSeconds}
        timeoutSeconds={timeoutTimeSeconds}
        ineffectiveSeconds={liveIneffectiveSeconds}
        ineffectiveBreakdown={ineffectiveBreakdown}
        advancedMetrics={advancedMetrics ?? null}
        currentClockSeconds={parseClockToSeconds(broadcastMatchClock)}
        currentPeriod={currentPeriod}
        matchFinished={matchFinished}
        t={t}
      />

      {getRenderedPeriods(allEvents).map((period) => (
        <PeriodMapsSection
          key={period}
          match={match}
          events={allEvents}
          period={period}
          t={t}
        />
      ))}

      <h3
        className="text-sm font-semibold text-slate-200 uppercase tracking-wider px-1"
        data-testid="period-maps-general-heading"
      >
        {t("analytics.periodMapsGeneral", "Generales")}
      </h3>
      <HeatMapSection match={match} events={allEvents} t={t} />

      <IneffectiveBreakdownSection
        match={match}
        ineffectiveBreakdown={ineffectiveBreakdown}
        ineffectiveSeconds={liveIneffectiveSeconds}
        varTimeSeconds={varTimeSeconds}
        timeoutSeconds={timeoutTimeSeconds}
        t={t}
      />

      {/* xG Race — shown when metrics are available (loading/error → hidden) */}
      {!advancedLoading && !advancedError && advancedMetrics && (
        <div
          className="rounded-lg bg-slate-900/60 border border-slate-800 px-4 py-3"
          data-testid="analytics-xg-race"
        >
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
            {t("advancedMetrics.xgRaceTitle", "Expected Goals (xG)")}
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="flex flex-col items-start gap-0.5">
              <span className="text-xs text-slate-400 truncate">
                {match.home_team.short_name || match.home_team.name}
              </span>
              <span
                className="text-2xl font-bold font-mono text-emerald-400"
                data-testid="analytics-xg-home"
              >
                {advancedMetrics.team_totals.home.xg.toFixed(2)}
              </span>
            </div>
            <span className="text-slate-400 text-sm font-semibold">xG</span>
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-xs text-slate-400 truncate">
                {match.away_team.short_name || match.away_team.name}
              </span>
              <span
                className="text-2xl font-bold font-mono text-blue-400"
                data-testid="analytics-xg-away"
              >
                {advancedMetrics.team_totals.away.xg.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      <div>
        <button
          type="button"
          data-testid="toggle-player-stats"
          onClick={() => setShowPlayerStats((prev) => !prev)}
          className="mb-2 min-h-11 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700"
        >
          {showPlayerStats
            ? t("analytics.hidePlayerStats", "Hide player statistics")
            : t("analytics.showPlayerStats", "Show player statistics")}
        </button>
        {showPlayerStats && (
          <PlayerStatsTable
            stats={playerStats}
            homeTeamName={match.home_team.short_name || match.home_team.name}
            awayTeamName={match.away_team.short_name || match.away_team.name}
            advancedPlayerStats={advancedPlayerStats}
            ratingByPlayerId={ratingByPlayerId}
            ratingLoading={ratingLoading}
            ratingError={ratingError}
            t={t}
          />
        )}
      </div>

      {/* Single-player analytics view (PMA-COCK-168): retires the old
          all-cards grid (`player-cards-grid`) in favor of a home/away +
          roster-chip picker showing one PlayerCard at a time. */}
      <PlayerCardPicker
        match={match}
        playerStats={playerStats}
        events={allEvents}
        currentClockSeconds={parseClockToSeconds(broadcastMatchClock)}
        currentPeriod={currentPeriod}
        matchFinished={matchFinished}
        t={t}
      />

      {/* AI Insights panel + chat — AC-5: only in AnalyticsView.
          Touch/small screens only: fixed at the bottom of this tab.
          Desktop gets the floating shell instead (FloatingAiChat at
          LoggerCockpit level); the in-tab panel is hidden there.
          PMA-COCK-179 (2026-09-10): whole surface hidden behind
          AI_INSIGHTS_ENABLED (default off, client request) — no wrapper
          rendered at all when off. */}
      {AI_INSIGHTS_ENABLED && !isDesktop && (
        <div
          className="sticky bottom-0 z-10 pb-1 pt-2 bg-slate-900/95 backdrop-blur"
          data-testid="ai-insights-fixed-shell"
        >
          <AiInsightsPanel matchId={match.id} t={t} />
        </div>
      )}
    </div>
  );
}
